// src/App.jsx
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Navbar from "./components/Navbar";
import PageParallaxBackground from "./components/PageParallaxBackground";
import Footer from "./components/Footer";

import LandingPage from "./pages/LandingPage";
import TeamLeitung from "./pages/TeamLeitung";
import SchulprofilWertePage from "./pages/Profil";
import RaeumeUmgebungPage from "./pages/RaeumeUmgebungPage";
import PartnerPage from "./pages/PartnerPage";
import LernenUndVerstehenPage from "./pages/LernenUndVerstehen";
import NachmittagsbetreuungPage from "./pages/NachmittagsbetreuungPage";
import EinblickePage from "./pages/EinblickePage";
import TerminePage from "./pages/TerminePage";
import FaqPage from "./pages/FaqPage";
import DownloadsFormularePage from "./pages/DownloadFormularePage";
import AnfahrtPage from "./pages/AnfahrtsPage";
import SprachenStaerkenPage from "./pages/SprachenStaerkenPage";
import WeltAlsLernraumPage from "./pages/WeltAlsLernraumPage";
import MitgestaltenUnterstuetzenPage from "./pages/MitgestaltenUnterstuetzenPage";
import ElternvereinPage from "./pages/ElternvereinPage";
import LegalPage from "./pages/LegalPage";
import BegabungKonzeptPage from "./pages/BegabungKonzeptPage";
import BegabungAngebotePage from "./pages/BegabungAngebotePage";
import BegabungProjektePage from "./pages/BegabungProjektePage";
import BeratungInformationenPage from "./pages/BeratungInformationenPage";

import { getGlobal, getSiteDesign, getMenuRoutes } from "./lib/api";
import "./App.css";

/**
 * Page registry: the keys are the values of the `page` enumeration on
 * MenuItem in Strapi. Add a new page here (and to the enumeration) to
 * make it selectable in the CMS.
 */
const PAGES = {
  landing:              () => <LandingPage />,
  team:                 () => <TeamLeitung />,
  profil:               () => <SchulprofilWertePage />,
  anfahrt:              () => <AnfahrtPage />,
  raeume:               () => <RaeumeUmgebungPage />,
  partner:              () => <PartnerPage />,
  lernen_verstehen:     () => <LernenUndVerstehenPage />,
  nachmittagsbetreuung: () => <NachmittagsbetreuungPage />,
  einblicke:            () => <EinblickePage />,
  termine:              () => <TerminePage />,
  faq:                  () => <FaqPage />,
  downloads:            () => <DownloadsFormularePage />,
  sprachen:             () => <SprachenStaerkenPage />,
  welt_lernraum:        () => <WeltAlsLernraumPage />,
  mitgestalten:         () => <MitgestaltenUnterstuetzenPage />,
  elternverein:         () => <ElternvereinPage />,
  beratung:             () => <BeratungInformationenPage />,
  konzept_ziele:        () => <BegabungKonzeptPage />,
  foerderangebote:      () => <BegabungAngebotePage />,
  projekte_erfolge:     () => <BegabungProjektePage />,
  impressum:            () => <LegalPage section="impressum" fallbackTitle="Impressum" />,
  datenschutz:          () => <LegalPage section="datenschutz" fallbackTitle="Datenschutz" />,
  cookies:              () => <LegalPage section="cookie" fallbackTitle="Cookies" />,
};

/**
 * Fixed fallback routes — always active, so the site works even before
 * the Strapi `page` field is filled in, and old links keep working after
 * a slug is changed in the CMS. Strapi routes are added on top.
 */
const DEFAULT_ROUTES = [
  { path: "/", page: "landing" },
  { path: "/team", page: "team" },
  { path: "/profil", page: "profil" },
  { path: "/anfahrt", page: "anfahrt" },
  { path: "/raeume", page: "raeume" },
  { path: "/partner", page: "partner" },
  { path: "/lernen-und-verstehen", page: "lernen_verstehen" },
  { path: "/nachmittagsbetreuung", page: "nachmittagsbetreuung" },
  { path: "/einblicke", page: "einblicke" },
  { path: "/termine", page: "termine" },
  { path: "/faq", page: "faq" },
  { path: "/downloads", page: "downloads" },
  { path: "/sprache", page: "sprachen" },
  { path: "/welt-lernraum", page: "welt_lernraum" },
  { path: "/mitgestalten", page: "mitgestalten" },
  { path: "/elternverein", page: "elternverein" },
  { path: "/beratung-information", page: "beratung" },
  { path: "/konzept-und-ziele", page: "konzept_ziele" },
  { path: "/foerderangebote", page: "foerderangebote" },
  { path: "/projekte-und-erfolge", page: "projekte_erfolge" },
  { path: "/impressum", page: "impressum" },
  { path: "/datenschutz", page: "datenschutz" },
  { path: "/cookies", page: "cookies" },
];

export default function App() {
  const [global, setGlobal] = useState({});
  const [menuRoutes, setMenuRoutes] = useState([]);

  useEffect(() => {
    getGlobal().then(setGlobal).catch(console.error);
  }, []);

  useEffect(() => {
    getMenuRoutes().then(setMenuRoutes).catch(console.error);
  }, []);

  useEffect(() => {
    getSiteDesign()
      .then((t) => {
        const root = document.documentElement.style;
        const map = {
          "--brand-red": t.accentColor,
          "--brand-blue": t.secondaryColor,
          "--rs-text": t.textColor,
          "--rs-border-base": t.borderColor,
          "--rs-tint": t.backgroundColor,
          "--rs-header-pad-top": t.headerPadTop,
          "--rs-header-pad-bottom": t.headerPadBottom,
          "--rs-header-pad-top-sm": t.headerPadTopSm,
          "--rs-header-pad-bottom-sm": t.headerPadBottomSm,
          "--rs-intro-pad-top": t.introPadTop,
          "--rs-intro-pad-bottom": t.introPadBottom,
          "--rs-intro-pad-top-sm": t.introPadTopSm,
          "--rs-intro-pad-bottom-sm": t.introPadBottomSm,
          "--rs-section-pad-top": t.sectionPadTop,
          "--rs-section-pad-bottom": t.sectionPadBottom,
          "--rs-section-pad-top-sm": t.sectionPadTopSm,
          "--rs-section-pad-bottom-sm": t.sectionPadBottomSm,
          "--rs-grid-gap": t.gridGap,
        };
        Object.entries(map).forEach(([k, v]) => root.setProperty(k, v));
      })
      .catch(console.error);
  }, []);

  // Strapi routes first, then the fixed ones; a path is only registered
  // once, and unknown page keys from the CMS are ignored.
  const routes = [];
  const seen = new Set();
  [...menuRoutes, ...DEFAULT_ROUTES].forEach((r) => {
    if (!PAGES[r.page] || seen.has(r.path)) return;
    seen.add(r.path);
    routes.push(r);
  });

  return (
    <BrowserRouter>
      <div className="site-shell">
        <PageParallaxBackground />

        <div className="site-content">
          <Navbar />

          <main className="site-main">
            <Routes>
              {routes.map((r) => (
                <Route key={r.path} path={r.path} element={PAGES[r.page]()} />
              ))}
            </Routes>
          </main>

          <Footer data={global} />
        </div>
      </div>
    </BrowserRouter>
  );
}